import React, {useEffect, useState} from 'react';
import {Navigate, useNavigate} from 'react-router-dom';
import {api} from '../api';

const apps = [
  { key: 'inventory', name: 'Inventory', description: 'Manage items, stock, and orders', href: '/inventory', color: 'bg-red-600' },
  { key: 'crm', name: 'CRM', description: 'Manage customers and relationships', href: '/crm', color: 'bg-blue-600' },
  { key: 'accounting', name: 'Accounting', description: 'Manage accounts, invoices, and reports', href: '/accounting', color: 'bg-emerald-600' },
  { key: 'vehicles', name: 'Vehicles', description: 'Manage vehicles and service records', href: '/vehicles', color: 'bg-purple-600' },
  { key: 'tasks', name: 'Tasks', description: 'Manage tasks and to-dos', href: '/tasks', color: 'bg-orange-600' },
  { key: 'storage', name: 'Storage', description: 'Manage storage locations', href: '/storage', color: 'bg-green-600' },
  { key: 'hcm', name: 'HCM', description: 'Human capital management', href: '/hcm', color: 'bg-indigo-600' },
  { key: 'ats', name: 'ATS', description: 'Applicant tracking system', href: '/ats', color: 'bg-teal-600' },
  { key: 'timesheets', name: 'Timesheets', description: 'Track employee time entries', href: '/timesheets', color: 'bg-teal-600' },
  { key: 'banned-list', name: 'Banned List', description: 'Track banned items and reasons', href: '/banned-list', color: 'bg-rose-600' },
  { key: 'documents', name: 'Documents', description: 'Upload and manage documents', href: '/documents', color: 'bg-cyan-600' },
  { key: 'lms', name: 'LMS', description: 'Learning management and training', href: '/lms', color: 'bg-violet-600' },
  { key: 'analytics', name: 'Analytics', description: 'Website analytics and call tracking', href: '/analytics', color: 'bg-amber-600' },
  { key: 'receipts', name: 'Receipts', description: 'Scan receipts and extract data', href: '/receipts', color: 'bg-lime-600' },
  { key: 'recordings', name: 'Recordings', description: 'Record audio and transcribe to text', href: '/recordings', color: 'bg-sky-600' },
  { key: 'policies', name: 'Policies', description: 'Manage and publish company policies', href: '/policies', color: 'bg-pink-600' },
  { key: 'estimates', name: 'Estimates', description: 'Create and send job estimates', href: '/estimates', color: 'bg-yellow-600' },
  { key: 'jobs', name: 'Jobs', description: 'Schedule and manage jobs', href: '/jobs', color: 'bg-teal-600' },
  { key: 'calendar', name: 'Calendar', description: 'Schedule and view events', href: '/calendar', color: 'bg-blue-600' },
  { key: 'marketing', name: 'Marketing', description: 'Manage marketing campaigns and ads', href: '/marketing', color: 'bg-fuchsia-600' },
];

function getCookie(name) {
  const match = document.cookie.split(';').map(c => c.trim()).find(c => c.startsWith(name + '='));
  return match ? match.split('=')[1] : null;
}

export default function AppSelector() {
  const navigate = useNavigate();
  const tenantId = getCookie('tenantId');
  const tenantName = getCookie('tenantName') ? decodeURIComponent(getCookie('tenantName')) : null;
  const [enabledApps, setEnabledApps] = useState(null);

  useEffect(() => {
    if (tenantId) {
      api(`/tenants/${tenantId}/apps`).then(r => r.ok ? r.json() : []).then(setEnabledApps).catch(() => setEnabledApps([]));
    }
  }, [tenantId]);

  if (!tenantId) return <Navigate to="/tenants" replace />;

  const handleLogout = () => {
    document.cookie = 'jwtToken=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
    document.cookie = 'renewalToken=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
    document.cookie = 'displayName=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
    document.cookie = 'tenantId=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
    document.cookie = 'tenantName=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
    navigate('/login');
  };

  const visibleApps = enabledApps === null ? [] : enabledApps.length === 0 ? apps : apps.filter(a => enabledApps.includes(a.key));

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center h-20">
          <img src="/images/matterking-logo.png" alt="Matter King" className="h-16 w-auto" />
          <div className="flex items-center gap-2 sm:gap-4 flex-wrap justify-end">
            {tenantName && <span className="text-sm text-gray-500 hidden sm:inline">{tenantName}</span>}
            <a href="/tenants" className="text-sm text-gray-600 hover:text-gray-900 font-medium">Switch</a>
            <a href="/settings" className="text-sm text-gray-600 hover:text-gray-900 font-medium">Settings</a>
            <button onClick={handleLogout} className="text-sm text-gray-600 hover:text-red-600 font-medium">Sign Out</button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-16">
        <h1 className="text-3xl font-bold text-gray-900 mb-8 text-center">Select an Application</h1>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          {visibleApps.map((app) => (
            <a key={app.key} href={app.href}
              className="block p-6 bg-white rounded-lg shadow hover:shadow-lg transition border-2 border-transparent hover:border-red-600">
              <div className={`w-12 h-12 ${app.color} rounded-lg mb-4 flex items-center justify-center text-white font-bold text-lg`}>
                {app.name[0]}
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">{app.name}</h2>
              <p className="text-gray-600">{app.description}</p>
            </a>
          ))}
        </div>
        {enabledApps !== null && enabledApps.length > 0 && visibleApps.length === 0 && (
          <p className="text-center text-gray-500 mt-8">No apps enabled for this organization.</p>
        )}
      </main>

      <footer className="text-center text-sm text-gray-400 py-8">
        &copy; 2026 Matter King, Inc. All rights reserved.
      </footer>
    </div>
  );
}
